<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, saveFormViaAPI, getFullUrl } from '../../functionCollection';
    import VueDatePicker from '@vuepic/vue-datepicker';
    import '@vuepic/vue-datepicker/dist/main.css'

    const props = defineProps(['fetchUrl', 'updateUrl', 'userNumber', 'currentUserIsAdmin']);
    const emit = defineEmits(['switch-to-show', 'successful-save', 'save-error']);

    const userInfo = ref(null);
    const showError = ref(false);
    const result = ref();

    const submitResult = ref(null);
    const submitError = ref(null);

    // On website load, fetch data, and format the birthday date correctly for VueDatePicker/JS
    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl, props.userNumber);
        userInfo.value = await getFromAPI(url);
        userInfo.value.Geburtsdatum = formatDate(userInfo.value.Geburtsdatum);
    });

    // format the received date to be usable by VueDatePicker
    function formatDate(date) {
        let res = '';
        res = res.concat(date.substring(6, 10), '-', date.substring(3, 5), '-', date.substring(0, 2));
        return res;
    }

    // save the changed user info.
    // if the save fails, then we emit that, so that UserData.vue will show a generic user error.
    async function saveForm() {
        result.value = null;
        const formData = JSON.parse(JSON.stringify(userInfo.value));
        result.value = await saveFormViaAPI(getFullUrl(props.updateUrl, props.userNumber), formData);
        if (result.value.success === true) {
            emit('successful-save');
            emit('switch-to-show');
        } else if (result.value.success === false) {
            submitError.value = result.value.data;
            emit('save-error');
        }
    }

</script>

<template>
    <h3>Meine Daten ändern</h3>
    <div class="well" v-if="userInfo">
        <p v-if="showError">Fehler beim Abspeichern der Daten. Bitte passieren Sie ihre Daten an oder versuchen Sie es später erneut.</p>
        <form>
            <p>Aktualisieren Sie Ihre Anschrift und Ihre persönlichen Daten:</p>
            <h4>Persönliches</h4>
            <div class="form-group row">
                <label class="col-sm-3 col-form-label">Anrede</label>
                <div class="col-sm-9">
                        <select v-model="userInfo.Anrede"
                            :class="{
                                'form-select form-control': true,
                                'is-valid': (submitError !== null && typeof submitError.response.data.Anrede === 'undefined'),
                                'is-invalid': (submitError !== null && typeof submitError.response.data.Anrede !== 'undefined'),
                            }">
                            <option value="Frau">Frau</option>
                            <option value="Herr">Herr</option>
                            <option value="Divers">Divers</option>
                            <option value="Firma">Firma</option>

                        </select>
                        <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Anrede !== 'undefined')">
                            {{ submitError.response.data.Anrede[0] }}
                        </div>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-form-label">Nachname</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Name1 === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Name1 !== 'undefined'),
                        }"
                        v-model="userInfo.Name1"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Name1 !== 'undefined')">
                        {{ submitError.response.data.Name1[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Vorname</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Name2 === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Name2 !== 'undefined'),
                        }"
                        v-model="userInfo.Name2"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Name2 !== 'undefined')">
                        {{ submitError.response.data.Name2[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Geburtsdatum</label>
                <div class="col-sm-9">
                    <VueDatePicker
                      v-model="userInfo.Geburtsdatum"
                      text-input
                      format="dd.MM.yyyy"
                      locale="de"
                      :enable-time-picker="false"
                      selectText="Auswählen"
                      cancelText="Abbrechen" />
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Geburtsdatum !== 'undefined')">
                        {{ submitError.response.data.Geburtsdatum[0] }}
                    </div>
                </div>
            </div>
            <h4>Adresse</h4>
            <div class="form-group row">
                <label class="col-sm-3 col-form-label">Straße</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Strasse === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Strasse !== 'undefined'),
                        }"
                        v-model="userInfo.Strasse"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Strasse !== 'undefined')">
                        {{ submitError.response.data.Strasse[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Postleitzahl</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.PLZ === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.PLZ !== 'undefined'),
                        }"
                        v-model="userInfo.PLZ"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.PLZ !== 'undefined')">
                        {{ submitError.response.data.PLZ[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Ort</label>
                <div class="col-sm-9">
                    <input v-model="userInfo.Ort"
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Ort === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Ort !== 'undefined'),
                        }"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Ort !== 'undefined')">
                        {{ submitError.response.data.Ort[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label" for="land">Land</label>
                <div class="col-sm-9">
                    <select v-model="userInfo.Land"
                        :class="{
                            'form-select form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Land === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Land !== 'undefined'),
                        }">
                        <option value="Deutschland">Deutschland</option>
                        <option value="Belgien">Belgien</option>
                        <option value="Niederlande">Niederlande</option>
                        <option value="Luxemburg">Luxemburg</option>
                        <option value="Frankreich">Frankreich</option>
                        <option value="Österreich">Österreich</option>
                        <option value="Schweiz">Schweiz</option>
                        <option value="Italien">Italien</option>
                        <option value="Rumänien">Rumänien</option>
                        <option value="Spanien">Spanien</option>
                    </select>
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Land !== 'undefined')">
                        {{ submitError.response.data.Land[0] }}
                    </div>
                </div>
            </div>
            <h4>Kontakt</h4>
            <div class="form-group row">
                <label class="col-sm-3 col-form-label">E-Mail-Adresse</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Email === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Email !== 'undefined'),
                        }"
                        v-model="userInfo.Email"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Email !== 'undefined')">
                        {{ submitError.response.data.Email[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Telefon</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Telefon1 === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Telefon1 !== 'undefined'),
                        }"
                        v-model="userInfo.Telefon1"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Telefon1 !== 'undefined')">
                        {{ submitError.response.data.Telefon1[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Zweite Telefonnummer</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Telefon2 === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Telefon2 !== 'undefined'),
                        }"
                        v-model="userInfo.Telefon2"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Telefon2 !== 'undefined')">
                        {{ submitError.response.data.Telefon2[0] }}
                    </div>
                </div>
                <label class="col-sm-3 col-form-label">Faxnummer</label>
                <div class="col-sm-9">
                    <input
                        :class="{
                            'form-control': true,
                            'is-valid': (submitError !== null && typeof submitError.response.data.Fax === 'undefined'),
                            'is-invalid': (submitError !== null && typeof submitError.response.data.Fax !== 'undefined'),
                        }"
                        v-model="userInfo.Fax"
                        type="text">
                    <div class="invalid-feedback" v-if="(submitError !== null && typeof submitError.response.data.Fax !== 'undefined')">
                        {{ submitError.response.data.Fax[0] }}
                    </div>
                </div>
                <label v-if="currentUserIsAdmin" class="col-sm-3 col-form-label">Nutzer benachrichtigen?</label>
                <div v-if="currentUserIsAdmin" class="col-sm-9 d-flex align-items-center">
                    <input type="checkbox" v-model="userInfo.Benachrichtigen">
                </div>
            </div>
        </form>
        <div class="table-footer">
            <button class="btn btn-link footer-button"
                type="submit"
                @click="saveForm">
                Speichern
                <i class="far fa-lg fa-save"></i>
            </button>
            <button class="btn btn-link footer-button"
                type="submit"
                @click="$emit('switch-to-show')">
                Abbrechen
                <i class="fas fa-lg fa-times"></i>
            </button>
        </div>
    </div>

</template>
